import { isNil } from 'ramda'
import numeral from 'numeral'

/**
 * main utility function for this file, formats a number into
 * a price using numeral package
 * @param {number} price | price to format
 * @param {string} defaultText | in case price is undefined
 * @param {boolean} isDecimals | should decimals be used or not
 * @returns {string}
 */
export const formatPrice = (price, defaultText, isDecimals = false) =>
  isNil(price)
    ? defaultText
    : isDecimals
    ? numeral(toFixed(2)(price)).format('$0,0.00')
    : numeral(toFixed(0)(price)).format('$0,0')

/**
 * sister utility function to formatPrice, formats a number into
 * a price but keeping the cents/decimal places using numeral package
 * @param {*} price | price to format
 * @param {*} defaultText | in case price is undefined
 * @returns {string} formatted price but with the cents included
 */
export const formatPriceCents = (price, defaultText = '') =>
  isNil(price) ? defaultText : numeral(toFixed(2)(price)).format('$0,0.00')

/**
 * helps when we want to round down a price
 * @param {number} price | price to format
 * @returns {string} rounded down price
 */
export const roundDown = price => {
  return isNil(price) || typeof price !== 'number'
    ? '$0'
    : Math.abs(price) < 1 && price !== 0
    ? numeral(toFixed(2)(price)).format('$0,0.00')
    : numeral(toFixed(0)(Math.floor(price))).format('$0,0')
}

/**
 * Numeral is having issue with rounding small numbers,
 *  so we will do our rounding before passing the value into numeral
 *  and after the PR is merged
 *  https://github.com/adamwdraper/Numeral-js/pull/635
 * @param {number} decimals | number of decimal places
 * @param {number} price | price to format
 * @returns {number} rounded and fixed decimal place number
 */
export const toFixed = decimals => price => {
  const power = Math.pow(10, decimals)
  return (Math.round(price * power) / power).toFixed(decimals)
}
